import { Chip, Grid, LinearProgress, Typography } from "@material-ui/core";
import { useSnackbar } from "notistack";
import * as React from "react";
import { useIntl } from "react-intl";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { archiveModule, createModule, deleteModule, getModule, publishModule, updateModule } from "../../api/ModuleApi";
import { NewEntityId } from "../../data/constants";
import { ModuleStateTranslationKey, ModuleStatus, ModuleViewModel, RequiredModule } from "../../model/Module";
import { routesPaths } from "../../Routes";
import { GetTranslation } from "../../translations/GetTranslation";
import { navigateBack } from "../../utils/Navigation";
import DefaultButtons from "../common/buttons/DefaultButtons";
import DefaultPublishArchiveButtons from "../common/buttons/DefaultPublishArchiveButtons";
import TranslatedMessage from "../translations/TranslatedMessage";
import { getInitialRequiredFieldsStatuses, initialModule } from "./ModuleConstants";
import ModuleFormExams from "./ModuleFormExams";
import ModuleFormFields from "./ModuleFormFields";

const ModuleForm = () => {
  const { moduleId } = useParams<{ moduleId?: string }>();
  const isCreating = moduleId === NewEntityId;

  const [isLoading, setIsLoading] = React.useState(!isCreating);
  const [module, setModule] = React.useState<ModuleViewModel>(initialModule);
  const [requiredFieldStatus, setRequiredFieldStatus] = React.useState(getInitialRequiredFieldsStatuses());

  const history = useHistory();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();

  const intl = useIntl();

  React.useEffect(() => {
    if (!isCreating && moduleId) {
      getModule(moduleId).then((result) => {
        setModule(result);
        setIsLoading(false);
      });
    }
  }, [isCreating, moduleId]);

  if (isLoading) {
    return (
      <div>
        <Typography variant="h5">
          <TranslatedMessage id={"module.header"} />
        </Typography>
        <LinearProgress variant="query" />
      </div>
    );
  }

  const handleSave = () => {
    if (!validateForm()) {
      return;
    }

    if (isCreating) {
      handleCreate();
    } else {
      handleUpdate();
    }
  };

  const handleCreate = () => {
    createModule(module).then((response) => {
      if (response.success) {
        navigateBack(routesPaths.modules(), history, location);
      }
    });
  };

  const handleUpdate = () => {
    updateModule(module).then((response) => {
      if (response.ok) {
        navigateBack(routesPaths.modules(), history, location);
      }
    });
  };

  const handlePublish = () => {
    publishModule(module).then((response) => {
      if (response.ok) {
        navigateBack(routesPaths.modules(), history, location);
        enqueueSnackbar(<TranslatedMessage id="module.snackbar.successfulPublish" />);
      }
    });
  };

  const handleDelete = () => {
    deleteModule(module.id).then((response) => {
      if (response.ok) {
        navigateBack(routesPaths.modules(), history, location);
        enqueueSnackbar(<TranslatedMessage id="module.snackbar.successfulDelete" />);
      }
    });
  };

  const handleArchive = () => {
    archiveModule(module).then((response) => {
      if (response.ok) {
        navigateBack(routesPaths.modules(), history, location);
        enqueueSnackbar(<TranslatedMessage id="module.snackbar.successfulArchive" />);
      }
    });
  };

  const validateForm = () => {
    let formHasEmptyFields = false;

    const isFieldEmpty = (value: string | number | undefined) => {
      const isEmpty = value === undefined || value === null || value === "";
      formHasEmptyFields = formHasEmptyFields || isEmpty;
      return isEmpty;
    };

    const currentStatus: RequiredModule = {
      isNameDEEmpty: isFieldEmpty(module.nameDE),
      isNameENEmpty: isFieldEmpty(module.nameEN),
      isNameFREmpty: isFieldEmpty(module.nameFR),
      isNameITEmpty: isFieldEmpty(module.nameIT),
      isPriceEmpty: isFieldEmpty(module.price),
      isGradingRuleEmpty: isFieldEmpty(module.gradingRule),
    };

    setRequiredFieldStatus(currentStatus);

    return !formHasEmptyFields;
  };

  return (
    <div>
      <Typography variant="h5">
        <TranslatedMessage id={"module.header"} />
      </Typography>

      <DefaultButtons
        additionalButtons={
          <React.Fragment>
            <Chip label={GetTranslation(intl, ModuleStateTranslationKey[module.status])} />
            <DefaultPublishArchiveButtons
              isArchiveButtonVisible={module.status === ModuleStatus.Published}
              isPublishButtonVisible={module.status === ModuleStatus.Draft && !isCreating}
              onPublish={handlePublish}
              onArchive={handleArchive}
            />
          </React.Fragment>
        }
        onClose={() => history.goBack()}
        onDelete={handleDelete}
        onSave={handleSave}
        isDeleteButtonVisible={module.status === ModuleStatus.Draft && !isCreating}
        isSaveButtonVisible={module.status === ModuleStatus.Draft || module.status === ModuleStatus.Published}
      />

      <Grid container={true} direction="column" spacing={4}>
        <Grid item={true}>
          <ModuleFormFields
            module={module}
            handleFieldChange={(change: Partial<ModuleViewModel>) => setModule({ ...module, ...change })}
            requiredFieldStatus={requiredFieldStatus}
          />
        </Grid>

        <Grid item={true}>
          {!isCreating && (
            <ModuleFormExams
              exams={module.exams}
              handleAddExams={(exams) => setModule({ ...module, exams: [...module.exams, ...exams] })}
              handleRemoveExam={(exam) => setModule({ ...module, exams: module.exams.filter((e) => e.id !== exam.id) })}
              isReadOnly={module.status === ModuleStatus.Archived}
            />
          )}
        </Grid>
      </Grid>
    </div>
  );
};

export default ModuleForm;
